import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';

import { Users } from '../pages/Users';
import { Groups } from '../pages/Groups';
import { Login } from '../pages/Login';
import { Options } from '../pages/Options';
import { Warehouses } from '../pages/Warehouses';
import { Swagger } from '../pages/Swagger';
import { PrivateRoute } from './PrivateRoute';
import { Dashboard } from '../pages/Dashboard';

import { TableViewsWrapper } from '../pages/TableViewsWrapper';
import { Logs } from '../pages/Logs';
import { CardAttempts } from '../pages/CardAttempts';
import { CardAttemptsFilterSelect } from '../components/CardAttemptsFilterSelect';
import { PrinterAttempts } from '../pages/PrinterAttempts';
import { PrinterAttemptsFilterSelect } from '../components/PrinterAttemptsFilterSelect';
import { MobileErrors } from '../pages/MobileErrors';
import { FleetPage } from '../pages/fleet/FleetPage';
import { MobileErrorsFilterSelect } from '../components/MobileErrorsFilterSelect';
import { LogsMultiSelect } from '../components/LogsMultiSelect';
import { DateFilterComponent } from '../components/DateFilterComponent';
import { UserRoleMultiSelect } from '../components/UserRoleMultiSelect';
import { WarehouseView } from '../pages/WarehouseView';
import { Passports } from '../pages/passports/Passports';
import { Inventory } from '../pages/Inventory';
import { UserInventoriesView } from '../pages/stock/UserInventoriesView';
import { HandoutRequests } from '../pages/stock/HandoutRequests';

import { AccessibilityCheckRoute } from './AccessibilityCheckRoute';
import { Role } from '../utils/Enums/Roles';
import { Tasks } from '../pages/Tasks';
import { DistributorSelectFilter } from '../components/DistributorSelectFilter';
import { References } from '../pages/references/References';
import { InvoicesPage } from '../pages/InvoicesPage';
import { Protocols, ProtocolTypeFilter } from '../pages/Protocols';
import { Revisions } from '../pages/Revisions';
import { FiscalDevices } from '../pages/FiscalDevices';
import Customers from '../pages/Customers';
import { PublicDocumentView } from '../pages/PublicDocumentView';
import { StuckPayments } from '../pages/StuckPayments';
import { NoAccessPage } from '../pages/NoAccessPage';
import { firstDayOfCurrentMonth } from '../utils/helper-functions';

function OpeningBalancesRedirect() {
    const [params] = useSearchParams();
    const next = new URLSearchParams(params);
    next.set('tab', 'opening-balances');
    return <Navigate replace to={`/invoices?${next.toString()}`} />;
}

export const Router = () => {
    return (
        <Routes>
            <Route path='/' element={<PrivateRoute />}>
                <Route path='/no-access' element={<NoAccessPage />} />
                <Route
                    path='/'
                    element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN, Role.COORDINATOR, Role.INSTALLER, Role.DISTRIBUTOR, Role.FINANCIAL]} />}
                >
                    <Route path='/' element={<Dashboard />} />
                    <Route path='/dashboard' element={<Dashboard />} />
                </Route>
                <Route path='/' element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN, Role.COORDINATOR, Role.WAREHOUSE, Role.FINANCIAL]} />}>
                    <Route
                        path='/warehouses'
                        element={<TableViewsWrapper tableComponent={Warehouses} title={'warehouses'} />}
                    />
                    <Route
                        path='/warehouses/:id'
                        element={<TableViewsWrapper tableComponent={WarehouseView} title={'stock'} />}
                    />

                    <Route
                        path='/inventories'
                        element={
                            <TableViewsWrapper
                                tableComponent={UserInventoriesView}
                                title={'inventories'}
                                searchAvailable={false}
                                pagerAvailable={false}
                            />
                        }
                    />
                    <Route
                        path='/inventories/:id'
                        element={
                            <TableViewsWrapper
                                tableComponent={UserInventoriesView}
                                title={'inventories'}
                                searchAvailable={false}
                                pagerAvailable={false}
                            />
                        }
                    />
                    <Route
                        path='/handout-requests'
                        element={
                            <TableViewsWrapper
                                tableComponent={HandoutRequests}
                                title={'handoutRequests'}
                                searchAvailable={false}
                            />
                        }
                    />
                </Route>
                <Route
                    path='/'
                    element={
                        <AccessibilityCheckRoute allowedRoles={[Role.ADMIN, Role.COORDINATOR, Role.WAREHOUSE, Role.DISTRIBUTOR, Role.INSTALLER, Role.FINANCIAL]} />
                    }
                >
                    <Route
                        path='/passports'
                        element={<TableViewsWrapper tableComponent={Passports} title={'passports'} />}
                    />
                </Route>
                <Route
                    path='/'
                    element={
                        <AccessibilityCheckRoute allowedRoles={[Role.DISTRIBUTOR, Role.INSTALLER]} forbiddenRoles={[Role.ADMIN]} />
                    }
                >
                    <Route
                        path='/inventory'
                        element={<TableViewsWrapper tableComponent={Inventory} title={'inventory'} />}
                    />
                </Route>
                <Route
                    path='/'
                    element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN, Role.COORDINATOR, Role.DISTRIBUTOR, Role.INSTALLER]} />}
                >
                    <Route
                        path='/tasks'
                        element={
                            <TableViewsWrapper
                                searchAvailable={true}
                                tableComponent={Tasks}
                                dateFilterComponent={DateFilterComponent}
                                dateFilterAllowFuture
                                selectComponent={DistributorSelectFilter}
                                title={'tasks'}
                                pagerAvailable={false}
                            />
                        }
                    />
                    <Route
                        path='/tasks/distributor/:assigneeId'
                        element={
                            <TableViewsWrapper
                                searchAvailable={true}
                                tableComponent={Tasks}
                                dateFilterComponent={DateFilterComponent}
                                dateFilterAllowFuture
                                title={'tasks'}
                                pagerAvailable={false}
                            />
                        }
                    />

                    <Route path='/groups' element={<TableViewsWrapper tableComponent={Groups} title={'groups'} />} />
                </Route>
                <Route
                    path='/'
                    element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN, Role.COORDINATOR, Role.DISTRIBUTOR, Role.INSTALLER, Role.FINANCIAL]} />}
                >
                    <Route path='/customers' element={<Customers />} />
                </Route>
                <Route path='/' element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN, Role.COORDINATOR, Role.FINANCIAL]} />}>
                    <Route path='/opening-balances' element={<OpeningBalancesRedirect />} />
                    <Route
                        path='/invoices'
                        element={
                            <TableViewsWrapper
                                dateFilterComponent={DateFilterComponent}
                                tableComponent={InvoicesPage}
                                title={'payments'}
                                initialFilter={{ after: firstDayOfCurrentMonth() }}
                            />
                        }
                    />
                </Route>
                <Route path='/' element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN, Role.WAREHOUSE, Role.COORDINATOR, Role.FINANCIAL]} />}>
                    <Route
                        path='/references'
                        element={
                            <TableViewsWrapper
                                dateFilterComponent={DateFilterComponent}
                                tableComponent={References}
                                title={'references'}
                                pagerAvailable={false}
                            />
                        }
                    />
                </Route>
                <Route path='/' element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN, Role.WAREHOUSE, Role.FINANCIAL, Role.COORDINATOR]} />}>
                    <Route
                        path='/protocols'
                        element={
                            <TableViewsWrapper
                                dateFilterComponent={DateFilterComponent}
                                tableComponent={Protocols}
                                selectComponent={DistributorSelectFilter}
                                manualSearchControlComponent={ProtocolTypeFilter}
                                title={'protocols'}
                                manualSearch
                            />
                        }
                    />
                    <Route
                        path='/revisions'
                        element={
                            <TableViewsWrapper
                                tableComponent={Revisions}
                                title={'revisions'}
                                pagerAvailable={false}
                                searchAvailable={false}
                            />
                        }
                    />
                </Route>
                <Route path='/' element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN]} />}>
                    <Route
                        path='/users'
                        element={
                            <TableViewsWrapper
                                tableComponent={Users}
                                selectComponent={UserRoleMultiSelect}
                                title={'users'}
                            />
                        }
                    />
                </Route>
                <Route path='/' element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN]} />}>
                    <Route
                        path='/fiscalDevices'
                        element={
                            <TableViewsWrapper
                                tableComponent={FiscalDevices}
                                title={'fiscalDevices'}
                            />
                        }
                    />
                </Route>
                <Route path='/' element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN]} />}>
                    <Route
                        path='/logs'
                        element={
                            <TableViewsWrapper
                                tableComponent={Logs}
                                selectComponent={LogsMultiSelect}
                                dateFilterComponent={DateFilterComponent}
                                title={'logs'}
                            />
                        }
                    />
                </Route>
                <Route path='/' element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN]} />}>
                    <Route path='/options' element={<Options />} />
                </Route>
                <Route path='/' element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN]} />}>
                    <Route path='/fleet' element={<FleetPage />} />
                </Route>
                {process.env.NODE_ENV !== 'production' && (
                    <Route path='/' element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN]} />}>
                        <Route
                            path='/card-attempts'
                            element={
                                <TableViewsWrapper
                                    tableComponent={CardAttempts}
                                    selectComponent={CardAttemptsFilterSelect}
                                    dateFilterComponent={DateFilterComponent}
                                    title={'cardAttempts'}
                                />
                            }
                        />
                    </Route>
                )}
                {process.env.NODE_ENV !== 'production' && (
                    <Route path='/' element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN]} />}>
                        <Route
                            path='/printer-attempts'
                            element={
                                <TableViewsWrapper
                                    tableComponent={PrinterAttempts}
                                    selectComponent={PrinterAttemptsFilterSelect}
                                    dateFilterComponent={DateFilterComponent}
                                    title={'printerAttempts'}
                                />
                            }
                        />
                    </Route>
                )}
                {process.env.NODE_ENV !== 'production' && (
                    <Route path='/' element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN]} />}>
                        <Route
                            path='/mobile-errors'
                            element={
                                <TableViewsWrapper
                                    tableComponent={MobileErrors}
                                    selectComponent={MobileErrorsFilterSelect}
                                    dateFilterComponent={DateFilterComponent}
                                    title={'mobileErrors'}
                                />
                            }
                        />
                    </Route>
                )}
                {process.env.NODE_ENV !== 'production' && (
                    <Route path='/' element={<AccessibilityCheckRoute allowedRoles={[Role.ADMIN]} />}>
                        <Route path='/stuck-payments' element={<StuckPayments />} />
                    </Route>
                )}
                {process.env.NODE_ENV !== 'production' && <Route path='/swagger' element={<Swagger />} />}
                <Route index element={<Login />} />
            </Route>
            <Route path='/login' element={<Login />} />
            {/* Public document visualization - no auth required */}
            <Route path='/docs/view/:token/:docTypeCode' element={<PublicDocumentView />} />
            <Route path='/docs/view/:token' element={<PublicDocumentView />} />
        </Routes>
    );
};
